import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { reviewService } from '../../services/reviewService';
import { approveReview, rejectReview } from '../adminSlice';

// Async thunks for review operations

// Get public product reviews
export const fetchProductReviewsPublic = createAsyncThunk(
  'reviews/fetchProductReviewsPublic',
  async ({ productId, params = {} }, { rejectWithValue }) => {
    try {
      const response = await reviewService.getProductReviewsPublic(productId, params);
      return { productId, ...response };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Get public seller reviews
export const fetchSellerReviewsPublic = createAsyncThunk(
  'reviews/fetchSellerReviewsPublic',
  async ({ sellerId, params = {} }, { rejectWithValue }) => {
    try {
      const response = await reviewService.getSellerReviewsPublic(sellerId, params);
      return { sellerId, ...response };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Get all reviews (authenticated)
export const fetchAllReviews = createAsyncThunk(
  'reviews/fetchAllReviews',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await reviewService.getAllReviews(params);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Create a new review
export const createReview = createAsyncThunk(
  'reviews/createReview',
  async (reviewData, { rejectWithValue }) => {
    try {
      const response = await reviewService.createReview(reviewData);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Update a review
export const updateReview = createAsyncThunk(
  'reviews/updateReview',
  async ({ reviewId, reviewData }, { rejectWithValue }) => {
    try {
      const response = await reviewService.updateReview(reviewId, reviewData);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Delete a review
export const deleteReview = createAsyncThunk(
  'reviews/deleteReview',
  async (reviewId, { rejectWithValue }) => {
    try {
      await reviewService.deleteReview(reviewId);
      return reviewId;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Get user's reviews
export const fetchUserReviews = createAsyncThunk(
  'reviews/fetchUserReviews',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await reviewService.getUserReviews(params);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Get seller's product reviews
export const fetchSellerProductReviews = createAsyncThunk(
  'reviews/fetchSellerProductReviews',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await reviewService.getSellerProductReviews(params);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Get product review analytics
export const fetchProductReviewAnalytics = createAsyncThunk(
  'reviews/fetchProductReviewAnalytics',
  async (productId, { rejectWithValue }) => {
    try {
      const response = await reviewService.getProductReviewAnalytics(productId);
      return { productId, analytics: response };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Get seller review analytics
export const fetchSellerReviewAnalytics = createAsyncThunk(
  'reviews/fetchSellerReviewAnalytics',
  async (sellerId, { rejectWithValue }) => {
    try {
      const response = await reviewService.getSellerReviewAnalytics(sellerId);
      return { sellerId, analytics: response };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Flag review (report)
export const flagReview = createAsyncThunk(
  'reviews/flagReview',
  async ({ reviewId, reason }, { rejectWithValue }) => {
    try {
      const response = await reviewService.flagReview(reviewId, reason);
      return { reviewId, ...response };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Add seller response to review
export const addSellerResponse = createAsyncThunk(
  'reviews/addSellerResponse',
  async ({ reviewId, response }, { rejectWithValue }) => {
    try {
      const apiResponse = await reviewService.addSellerResponse(reviewId, response);
      return { reviewId, response, ...apiResponse };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Mark review as helpful
export const markReviewHelpful = createAsyncThunk(
  'reviews/markReviewHelpful',
  async ({ reviewId, helpful }, { rejectWithValue }) => {
    try {
      const response = await reviewService.markReviewHelpful(reviewId, helpful);
      return { reviewId, helpful, ...response };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Upload review images
export const uploadReviewImages = createAsyncThunk(
  'reviews/uploadReviewImages',
  async (files, { rejectWithValue }) => {
    try {
      const response = await reviewService.uploadReviewImages(files);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

const initialState = {
  // Reviews data
  reviews: [],
  userReviews: [],
  sellerReviews: [],
  productReviews: {},
  
  // Analytics data
  productAnalytics: {},
  sellerAnalytics: {},
  
  // Pagination
  pagination: {
    page: 0,
    size: 10,
    totalElements: 0,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false,
  },
  
  // UI state
  loading: false,
  submitting: false,
  uploading: false,
  error: null,
  
  // Filters and sorting
  filters: {
    status: 'all',
    rating: 'all',
    sortBy: 'createdAt',
    sortDir: 'desc',
  },
  
  // Form state
  reviewForm: {
    rating: 0,
    title: '',
    comment: '',
    pros: '',
    cons: '',
    wouldRecommend: true,
    images: [],
  },
  
  // Selected items
  selectedReview: null,
  selectedProduct: null,
  selectedSeller: null,
};

const reviewSlice = createSlice({
  name: 'reviews',
  initialState,
  reducers: {
    // UI actions
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    
    // Filter and sort actions
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },
    
    // Form actions
    setReviewForm: (state, action) => {
      state.reviewForm = { ...state.reviewForm, ...action.payload };
    },
    resetReviewForm: (state) => {
      state.reviewForm = initialState.reviewForm;
    },
    
    // Selection actions
    setSelectedReview: (state, action) => {
      state.selectedReview = action.payload;
    },
    setSelectedProduct: (state, action) => {
      state.selectedProduct = action.payload;
    },
    setSelectedSeller: (state, action) => {
      state.selectedSeller = action.payload;
    },
    
    // Local review updates
    updateReviewLocally: (state, action) => {
      const { reviewId, updates } = action.payload;
      
      // Update in main reviews array
      const reviewIndex = state.reviews.findIndex(r => r.id === reviewId);
      if (reviewIndex !== -1) {
        state.reviews[reviewIndex] = { ...state.reviews[reviewIndex], ...updates };
      }
      
      // Update in user reviews
      const userReviewIndex = state.userReviews.findIndex(r => r.id === reviewId);
      if (userReviewIndex !== -1) {
        state.userReviews[userReviewIndex] = { ...state.userReviews[userReviewIndex], ...updates };
      }
      
      // Update in product reviews
      Object.keys(state.productReviews).forEach(productId => {
        const productReviewIndex = state.productReviews[productId].findIndex(r => r.id === reviewId);
        if (productReviewIndex !== -1) {
          state.productReviews[productId][productReviewIndex] = {
            ...state.productReviews[productId][productReviewIndex],
            ...updates
          };
        }
      });
    },
    
    // Remove review locally
    removeReviewLocally: (state, action) => {
      const reviewId = action.payload;
      
      // Remove from main reviews array
      state.reviews = state.reviews.filter(r => r.id !== reviewId);
      
      // Remove from user reviews
      state.userReviews = state.userReviews.filter(r => r.id !== reviewId);
      
      // Remove from product reviews
      Object.keys(state.productReviews).forEach(productId => {
        state.productReviews[productId] = state.productReviews[productId].filter(r => r.id !== reviewId);
      });
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch product reviews public
      .addCase(fetchProductReviewsPublic.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProductReviewsPublic.fulfilled, (state, action) => {
        state.loading = false;
        const { productId, reviews, analytics, ...pagination } = action.payload;
        state.productReviews[productId] = reviews || [];
        state.productAnalytics[productId] = analytics;
        state.pagination = {
          page: pagination.currentPage || 0,
          size: pagination.size || 10,
          totalElements: pagination.totalItems || 0,
          totalPages: pagination.totalPages || 0
        };
      })
      .addCase(fetchProductReviewsPublic.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch seller reviews public
      .addCase(fetchSellerReviewsPublic.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSellerReviewsPublic.fulfilled, (state, action) => {
        state.loading = false;
        const { sellerId, reviews, analytics, ...pagination } = action.payload;
        state.sellerReviews = reviews || [];
        state.sellerAnalytics[sellerId] = analytics;
        state.pagination = {
          page: pagination.currentPage || 0,
          size: pagination.size || 10,
          totalElements: pagination.totalItems || 0,
          totalPages: pagination.totalPages || 0
        };
      })
      .addCase(fetchSellerReviewsPublic.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch all reviews
      .addCase(fetchAllReviews.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllReviews.fulfilled, (state, action) => {
        state.loading = false;
        const { content, ...pagination } = action.payload;
        state.reviews = content || [];
        state.pagination = pagination;
      })
      .addCase(fetchAllReviews.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Create review
      .addCase(createReview.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(createReview.fulfilled, (state, action) => {
        state.submitting = false;
        state.reviews.unshift(action.payload);
        state.userReviews.unshift(action.payload);
        state.reviewForm = initialState.reviewForm;
      })
      .addCase(createReview.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
      })
      
      // Update review
      .addCase(updateReview.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(updateReview.fulfilled, (state, action) => {
        state.submitting = false;
        const updatedReview = action.payload;
        
        // Update in all relevant arrays
        const updateInArray = (array) => {
          const index = array.findIndex(r => r.id === updatedReview.id);
          if (index !== -1) {
            array[index] = updatedReview;
          }
        };
        
        updateInArray(state.reviews);
        updateInArray(state.userReviews);
        
        Object.keys(state.productReviews).forEach(productId => {
          updateInArray(state.productReviews[productId]);
        });
      })
      .addCase(updateReview.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
      })
      
      // Delete review
      .addCase(deleteReview.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteReview.fulfilled, (state, action) => {
        state.loading = false;
        const reviewId = action.payload;
        
        // Remove from all arrays
        state.reviews = state.reviews.filter(r => r.id !== reviewId);
        state.userReviews = state.userReviews.filter(r => r.id !== reviewId);
        
        Object.keys(state.productReviews).forEach(productId => {
          state.productReviews[productId] = state.productReviews[productId].filter(r => r.id !== reviewId);
        });
      })
      .addCase(deleteReview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch user reviews
      .addCase(fetchUserReviews.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserReviews.fulfilled, (state, action) => {
        state.loading = false;
        const { content, ...pagination } = action.payload;
        state.userReviews = content || [];
        state.pagination = pagination;
      })
      .addCase(fetchUserReviews.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch product analytics
      .addCase(fetchProductReviewAnalytics.fulfilled, (state, action) => {
        const { productId, analytics } = action.payload;
        state.productAnalytics[productId] = analytics;
      })
      
      // Fetch seller analytics
      .addCase(fetchSellerReviewAnalytics.fulfilled, (state, action) => {
        const { sellerId, analytics } = action.payload;
        state.sellerAnalytics[sellerId] = analytics;
      })
      
      // Upload images
      .addCase(uploadReviewImages.pending, (state) => {
        state.uploading = true;
        state.error = null;
      })
      .addCase(uploadReviewImages.fulfilled, (state, action) => {
        state.uploading = false;
        // Add uploaded image URLs to form
        state.reviewForm.images = [...state.reviewForm.images, ...action.payload.imageUrls];
      })
      .addCase(uploadReviewImages.rejected, (state, action) => {
        state.uploading = false;
        state.error = action.payload;
      })
      
      // Approve/Reject reviews
      .addCase(approveReview.fulfilled, (state, action) => {
        const { reviewId, data } = action.payload;
        const approvedReview = data.review;
        
        if (approvedReview && approvedReview.productId) {
          // Add the approved review to the product's review list
          if (!state.productReviews[approvedReview.productId]) {
            state.productReviews[approvedReview.productId] = [];
          }
          
          // Check if review already exists in the product reviews
          const existingIndex = state.productReviews[approvedReview.productId].findIndex(
            r => r.id === approvedReview.id
          );
          
          if (existingIndex !== -1) {
            // Update existing review
            state.productReviews[approvedReview.productId][existingIndex] = approvedReview;
          } else {
            // Add new approved review to the beginning of the list
            state.productReviews[approvedReview.productId].unshift(approvedReview);
          }
        }
        
        // Also update in other review arrays if they exist
        reviewSlice.caseReducers.updateReviewLocally(state, {
          payload: { reviewId, updates: approvedReview }
        });
      })
      .addCase(rejectReview.fulfilled, (state, action) => {
        const { reviewId, data } = action.payload;
        const rejectedReview = data.review;
        
        if (rejectedReview && rejectedReview.productId) {
          // Remove the rejected review from the product's review list
          if (state.productReviews[rejectedReview.productId]) {
            state.productReviews[rejectedReview.productId] = state.productReviews[rejectedReview.productId].filter(
              r => r.id !== rejectedReview.id
            );
          }
        }
        
        // Also update in other review arrays if they exist
        reviewSlice.caseReducers.updateReviewLocally(state, {
          payload: { reviewId, updates: rejectedReview }
        });
      })
      
      // Mark helpful
      .addCase(markReviewHelpful.fulfilled, (state, action) => {
        const { reviewId, review } = action.payload;
        if (review) {
          // Update with the actual review data from backend
          reviewSlice.caseReducers.updateReviewLocally(state, {
            payload: {
              reviewId,
              updates: {
                helpfulCount: review.helpfulCount || 0,
                notHelpfulCount: review.notHelpfulCount || 0,
                helpfulVoters: review.helpfulVoters || [],
                notHelpfulVoters: review.notHelpfulVoters || []
              }
            }
          });
        }
      })
      
      // Flag review
      .addCase(flagReview.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(flagReview.fulfilled, (state, action) => {
        state.submitting = false;
        const { reviewId } = action.payload;
        // Update review status to flagged
        reviewSlice.caseReducers.updateReviewLocally(state, {
          payload: {
            reviewId,
            updates: { status: 'FLAGGED' }
          }
        });
      })
      .addCase(flagReview.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
      })
      
      // Add seller response
      .addCase(addSellerResponse.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(addSellerResponse.fulfilled, (state, action) => {
        state.submitting = false;
        const { reviewId, response } = action.payload;
        // Update review with seller response
        reviewSlice.caseReducers.updateReviewLocally(state, {
          payload: {
            reviewId,
            updates: { 
              sellerResponse: response,
              sellerResponseDate: new Date().toISOString()
            }
          }
        });
      })
      .addCase(addSellerResponse.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
      });
  },
});

export const {
  setLoading,
  setError,
  clearError,
  setFilters,
  resetFilters,
  setReviewForm,
  resetReviewForm,
  setSelectedReview,
  setSelectedProduct,
  setSelectedSeller,
  updateReviewLocally,
  removeReviewLocally,
} = reviewSlice.actions;

export default reviewSlice.reducer;